import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { navigate, useRoute } from '../lib/router';
import { keep, recall, useRemembered } from '../lib/remember';
import { useNotice } from '../shell/notice';
import { useFlag, useMatches, useWide } from '../shell/hooks';
import { Chip, ChoiceChips, InterestDots } from '../ui/bits';
import { Segmented } from '../ui/Segmented';
import { INTEREST_HINTS, interestHint } from '../book/changes';
import { ChipsEditor, nameOf, useLists } from '../book/edit';
import { AUDIENCE_CHOICES, LITERARY_CHOICES, genreChoices, type Literary } from '../book/genres';
import { loadBook } from '../book/load';
import { bookLabel, dayText, statusName, todayIn } from '../book/model';
import type { BookData } from '../book/types';
import type { ShelfBook } from '../shelves/model';
import { BookSheet, type BookSheetKind } from './BookSheet';
import { InterestMenu, MoveMenu, NARROW_INTEREST, TypeMenu, flipSort, useTopLine } from './parts';
import { PillMenu, SettingMenu } from '../ui/PillMenu';
import { ListFoot } from '../ui/ListFoot';
import { BookCell, ListRow, ListTable, type Col } from '../ui/ListTable';
import { BackLink } from '../ui/BackLink';
import { addedText } from './recent';
import { num, titleKey } from './model';
import { CARD_FIELD, FIELD_NAMES, cycleSort, fixGroups, ruleName, newBooks, reviewPlan, sortList, startWhere, valueText, whereChoices, type Draft, type FixGroup, type Suggestion, type Where } from './review';
import { acceptBatches, callAction, markChanges, saveReview, unaccept, undoAction, undoReview } from './reviewLoad';
import { loadDetails, type Details } from '../shelves/load';
import { LOOKUP_CHIPS, shelfRank, type Dismissal, type LookupChip, type LookupItem } from './lookups';
import { LookupList } from './LookupList';

// My books › Recently added › Review (moved from the Queue Oct 31) (Phase 6.3 Part 4, step 1; Kristi, Oct 30, from the chat
// mockup). Suggested in a fix (grouped by the rule, Accept all or Look through them) and New
// books (a quick list or one card at a time): nothing is saved until Save; Save marks the book
// reviewed, and one Undo takes the whole review back. Step 2 (Oct 30) adds the six lookup chips
// (LookupList.tsx): each answer saves at once and doesn't mark the book reviewed.

type Chip = 'shelf' | 'fix' | 'new' | LookupChip;
const isLookup = (c: Chip): c is LookupChip => c !== 'fix' && c !== 'new' && c !== 'shelf';

export function ReviewView({
  books,
  typedLists,
  readTypes,
  suggestions,
  setSuggestions,
  reload,
  items,
  dismissals,
  setDismissals,
  reloadLookups,
  noShelf,
}: {
  books: ShelfBook[];
  /** Typed-list imports: their books count as New books (Oct 31). */
  typedLists?: Set<string>;
  readTypes: string[];
  /** The lookup questions per chip (null while loading). */
  items: Record<LookupChip, LookupItem[]> | null;
  dismissals: Dismissal[];
  setDismissals: (f: (d: Dismissal[]) => Dismissal[]) => void;
  reloadLookups: () => Promise<void>;
  /** Your waiting suggestions (read by Recently added, which shows the count on Review). */
  suggestions: Suggestion[] | null;
  setSuggestions: (f: (s: Suggestion[] | null) => Suggestion[] | null) => void;
  reload: () => Promise<void>;
  /** Update details' first list (filters review, Kristi Nov 2): books with no shelf yet, drawn by Recently added's table. */
  noShelf: { count: number; render: () => ComponentChildren };
}) {
  const notice = useNotice();
  const [chip, setChip] = useRemembered<Chip>('queue:review:chip', 'fix');
  // The fix being looked through: its name and books as they were when you opened it, so the count holds.
  // Kept with each book's suggestion as it was, so a handled row still shows "Study → Demanding", and
  // remembered on the device, so Back from a book page (phone) returns to the same fix.
  const [looking, setLookingRaw] = useRemembered<Looking | null>('queue:review:fix', null);
  const setLooking = (g: FixGroup | null) => {
    // Opening a fix starts at 20 rows again (Kristi, Oct 30); only Back from a book page keeps the count.
    if (g) keep('queue:review:shown', { name: g.name, n: 20 });
    return setLookingRaw(
      g
        ? {
            name: g.name,
            ids: g.bookIds,
            snap: Object.fromEntries(
              g.bookIds.map((id) => {
                const x = (suggestions ?? []).find((y) => y.bookId === id && y.batchKind !== 'nightly_new_books' && ruleName(y.batchName) === g.name);
                return [id, { field: x?.field ?? '', before: x?.before ?? null, value: x?.value ?? null }];
              }),
            ),
          }
        : null,
    );
  };
  // Rows handled in this visit, with their Undo (Kristi, Oct 30); kept while you look at a card and come back.
  const [handled, setHandled] = useState<Record<string, RowDone>>({});
  const [done, setDone] = useState<Set<string>>(new Set());
  // A book opened from a fix's quick list, shown as the one-book card.
  const [opened, setOpened] = useState<string | null>(null);
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  // New books: a quick list by default, or one at a time (Kristi, Oct 30).
  const [newView, setNewView] = useRemembered<'list' | 'one'>('queue:review:newview', 'list');
  // The new books as they were when the list opened, so a reviewed row stays until you leave.
  const [newIds, setNewIds] = useState<string[] | null>(null);
  // New books saved here (for "3 of 10" while the list shrinks).
  const [savedNew, setSavedNew] = useState(0);

  // A lookup chip's rows as they were when you opened it, so an answered row stays (✓) until you leave.
  const [answered, setAnswered] = useState<Set<string>>(new Set());
  const [lookupSnap, setLookupSnap] = useState<LookupItem[] | null>(null);
  useEffect(() => {
    if (isLookup(chip) && items && lookupSnap === null) setLookupSnap(items[chip]);
  }, [chip, items]);
  const lookupCount = (c: LookupChip) => (items ? items[c].filter((x) => !answered.has(x.key)).length : null);

  const groups = useMemo(() => fixGroups(suggestions ?? []), [suggestions]);
  const byId = useMemo(() => new Map(books.map((b) => [b.id, b])), [books]);
  const fresh = useMemo(() => newBooks(books, typedLists).filter((b) => !done.has(b.id)), [books, done, typedLists]);
  const fixBooks = new Set(groups.flatMap((g) => g.bookIds));
  const group = looking;

  // The books the card walks through: a fix's books, or the new books; skipped ones go last.
  const queue = useMemo(() => {
    const ids = chip === 'fix' ? (group ? group.ids.filter((id) => !done.has(id)) : []) : fresh.map((b) => b.id);
    return [...ids.filter((id) => !skipped.has(id)), ...ids.filter((id) => skipped.has(id))].filter((id) => byId.has(id));
  }, [chip, group, fresh, done, skipped, byId]);

  // Opening Review lands where something waits (coordinator, Nov 2): the remembered list if it still
  // has books, else the first list with any, To review before Lookups; Suggested in a fix when none do.
  const landed = useRef(false);
  useEffect(() => {
    if (landed.current || suggestions === null || items === null) return;
    landed.current = true;
    const waiting = (c: Chip) => (c === 'shelf' ? noShelf.count : c === 'fix' ? fixBooks.size : c === 'new' ? fresh.length : lookupCount(c) ?? 0);
    if (waiting(chip) > 0) return;
    const first = (['shelf', 'fix', 'new', ...LOOKUP_CHIPS.map((c) => c.key)] as Chip[]).find((c) => waiting(c) > 0) ?? 'fix';
    if (first !== chip) pick(first);
  }, [suggestions, items]);

  const pick = (c: Chip) => {
    setChip(c);
    setLookupSnap(isLookup(c) && items ? items[c] : null);
    setAnswered(new Set());
    setNewIds(null);
    setSavedNew(0);
    setLooking(null);
    setOpened(null);
    setSkipped(new Set());
  };

  const acceptAll = async (g: FixGroup) => {
    try {
      const marks = await acceptBatches(g.batchIds);
      setSuggestions((s) => (s ?? []).filter((x) => !g.batchIds.includes(x.batchId)));
      notice({
        message: `Accepted ${num(new Set(g.bookIds).size)} suggestions: ${g.name}`,
        undo: () =>
          void unaccept(marks)
            .then(() => reload())
            .then(() => notice({ message: `Undone: ${g.name} suggested again` })),
      });
    } catch (e) {
      notice({ message: `Not saved: ${e instanceof Error ? e.message : String(e)}`, done: false });
    }
  };

  const groupNow = group ? groups.find((g) => g.name === group.name) ?? null : null;
  // One fix waiting: straight to its table, no list of fixes first (Kristi, Oct 31). With several,
  // the list is how you choose one.
  const onlyFix = groups.length === 1;
  useEffect(() => {
    if (chip === 'fix' && !looking && suggestions !== null && onlyFix) setLooking(groups[0]!);
  }, [chip, looking, suggestions, onlyFix]);
  const listIds = newIds ?? fresh.map((b) => b.id);
  useEffect(() => {
    if (chip === 'new' && newView === 'list' && newIds === null && books.length) setNewIds(fresh.map((b) => b.id));
  }, [chip, newView, books.length]);
  const current = opened ? byId.get(opened) ?? null : chip === 'fix' ? null : newView === 'list' ? null : queue[0] ? byId.get(queue[0]) ?? null : null;
  const total = chip === 'fix' ? group?.ids.filter((id) => byId.has(id)).length ?? 0 : fresh.length + savedNew;
  const position = current ? total - queue.length + 1 : 0;

  return (
    <section class="rw" aria-label="Review">
      {/* Review is a tab now (Kristi, Oct 31), so the tabs are the way in and out. Inside, the one
          back link (R8 W5, Kristi Nov 1), top left, one step at a time: from a book opened from a
          list, back to that list; inside a fix (when there are several), back to every fix. */}
      {opened && (chip === 'fix' ? group : newView === 'list') ? (
        <BackLink where={chip === 'fix' ? 'the fix’s list' : 'New books'} onClick={() => setOpened(null)} />
      ) : chip === 'fix' && group && !onlyFix ? (
        <BackLink where="Suggested in a fix" onClick={() => setLooking(null)} />
      ) : null}
      {/* The fix's name is said once, by its table's heading under the switch (Kristi, Nov 3). */}
      {/* One view switch in two groups (R3 P3, Kristi Nov 2, answer 4a): the two lists you review,
          then the lookups. Each with its count and, while books wait there, the dot naming them
          (R7 N3, N9). A lookup with nothing waiting is hidden (Kristi, Oct 30), unless it's the one
          open. On a phone it stays on one line and scrolls with the edge (R9 M2). */}
      <div class="rw-switch">
        <Segmented<Chip>
          label="What to review"
          edge
          stacked
          value={chip}
          onChange={pick}
          groups={[
            {
              label: 'To review',
              // Every list here is worth a look (filters review, Kristi Nov 2): the ring, never the dot.
              options: [
                ...(noShelf.count || chip === 'shelf' ? [{ key: 'shelf' as Chip, name: 'No shelf yet', count: noShelf.count, worth: noShelf.count ? `Worth a look: ${num(noShelf.count)} with no shelf yet` : undefined }] : []),
                { key: 'fix', name: 'Suggested in a fix', count: suggestions ? fixBooks.size : undefined, worth: suggestions && fixBooks.size ? `Worth a look: ${num(fixBooks.size)} in Suggested in a fix` : undefined },
                { key: 'new', name: 'New books', count: fresh.length, worth: fresh.length ? `Worth a look: ${num(fresh.length)} in New books` : undefined },
              ],
            },
            {
              label: 'Lookups',
              options: LOOKUP_CHIPS.filter((c) => chip === c.key || lookupCount(c.key) === null || lookupCount(c.key)! > 0).map((c) => {
                const n = lookupCount(c.key);
                return { key: c.key, name: c.name, count: n ?? undefined, worth: n ? `Worth a look: ${num(n)} in ${c.name}` : undefined };
              }),
            },
          ]}
        />
      </div>
      {chip === 'shelf' ? (
        noShelf.render()
      ) : isLookup(chip) ? (
        lookupSnap === null ? (
          <p class="shelves-quiet">Loading…</p>
        ) : (
          <LookupList
            key={chip}
            chip={chip}
            items={lookupSnap}
            dismissals={dismissals}
            setDismissals={setDismissals}
            onAnswered={(k) => setAnswered((a) => new Set([...a, k]))}
            onUndone={async (k) => {
              setAnswered((a) => {
                const n = new Set(a);
                n.delete(k);
                return n;
              });
              await reloadLookups();
            }}
          />
        )
      ) : suggestions === null ? (
        <p class="shelves-quiet">Loading…</p>
      ) : chip === 'fix' && !group ? (
        <div class="rw-box">
          <div class="rw-box-head">
            <span class="rw-box-name">Suggested in a fix</span>
            <span class="rw-quiet">These books already have the value your rule set. Accept confirms it; Change picks another.</span>
          </div>
          {groups.length ? (
            groups.map((g) => (
              <div key={g.key} class="rw-group" data-group={g.key}>
                <div class="rw-group-text">
                  <div>
                    {g.name} <span class="rw-quiet">· {dayText(g.on, todayIn())}</span>
                  </div>
                  <div class="rw-quiet">
                    {g.what} · {num(g.bookIds.length)} {g.bookIds.length === 1 ? 'book' : 'books'}
                  </div>
                </div>
                <button type="button" class="btn-secondary" onClick={() => setLooking(g)}>
                  Look through them
                </button>
                <button type="button" class="btn-secondary" onClick={() => void acceptAll(g)}>
                  Accept all {num(g.bookIds.length)}
                </button>
              </div>
            ))
          ) : (
            <p class="rw-quiet rw-empty">Nothing waiting: every suggestion from a fix has been accepted.</p>
          )}
          {groups.length ? <p class="rw-quiet rw-foot">Accept all is one Undo.</p> : null}
        </div>
      ) : chip === 'fix' && group && !opened ? (
        <FixList
          name={group.name}
          ids={group.ids.filter((id) => byId.has(id))}
          byId={byId}
          suggestions={(suggestions ?? []).filter((x) => x.batchKind !== 'nightly_new_books' && ruleName(x.batchName) === group.name)}
          snap={group.snap ?? {}}
          handled={handled}
          setHandled={setHandled}
          readTypes={readTypes}
          onOpen={setOpened}
          onAcceptRemaining={groupNow ? () => void acceptAll(groupNow) : undefined}
          setSuggestions={setSuggestions}
          reload={reload}
        />
      ) : chip === 'new' && newView === 'list' && !opened ? (
        <NewList
          ids={listIds.filter((id) => byId.has(id))}
          byId={byId}
          suggestions={(suggestions ?? []).filter((x) => x.batchKind === 'nightly_new_books' || listIds.includes(x.bookId))}
          handled={handled}
          setHandled={setHandled}
          readTypes={readTypes}
          onOpen={setOpened}
          onOneAtATime={() => setNewView('one')}
          setSuggestions={setSuggestions}
          reload={reload}
        />
      ) : current ? (
        <>
        <ReviewCard
          topRight={chip === 'new' && !opened ? <ViewSwitch value="one" onPick={(v) => setNewView(v)} /> : undefined}
          key={current.id}
          head={current}
          readTypes={readTypes}
          suggestions={(suggestions ?? []).filter((s) => s.bookId === current.id)}
          showBefore={chip === 'fix'}
          where={chip === 'fix' && group ? group.name : opened ? 'New books' : `New books · ${position} of ${total}`}
          onAcceptRemaining={chip === 'fix' && groupNow ? () => void acceptAll(groupNow).then(() => setOpened(null)) : undefined}
          remaining={chip === 'fix' ? groupNow?.bookIds.length ?? 0 : 0}
          onSkip={() => (opened ? setOpened(null) : setSkipped(new Set([...skipped, current.id])))}
          onSaved={(ids) => {
            if (opened) {
              setHandled({ ...handled, [current.id]: { kind: 'reviewed', marks: [] } });
              setOpened(null);
            }
            setDone(new Set([...done, current.id]));
            if (chip === 'new') setSavedNew(savedNew + 1);
            setSuggestions((s) => (s ?? []).filter((x) => !ids.includes(x.id)));
          }}
          onUndone={() => {
            const d = new Set(done);
            d.delete(current.id);
            setDone(d);
            if (chip === 'new') setSavedNew(Math.max(0, savedNew - 1));
            void reload();
          }}
        />
        </>
      ) : (
        <div class="rw-box">
          <p class="rw-quiet rw-empty">
            {chip === 'fix' ? 'All done for this fix.' : 'No new books to review: everything you’ve added has been reviewed.'}
          </p>
        </div>
      )}
    </section>
  );
}

const draftOf = (b: BookData, status: string | null): Draft => ({
  literary: b.work.fiction,
  // Adult unless set (Kristi, Oct 30): saved with the review, so a reviewed book has an audience.
  audience: b.work.audience ?? 'adult',
  genres: b.genres,
  subgenres: b.subgenres,
  readType: b.readType?.name ?? null,
  tags: b.tags,
  interest: b.interest,
  where: startWhere(status),
});

function ReviewCard({
  head,
  readTypes,
  suggestions,
  showBefore,
  where: whereLine,
  topRight,
  onAcceptRemaining,
  remaining = 0,
  onSkip,
  onSaved,
  onUndone,
}: {
  head: ShelfBook;
  readTypes: string[];
  suggestions: Suggestion[];
  showBefore: boolean;
  where: string;
  /** Inside the card's top line, at the right: New books' List · One at a time (Kristi, Oct 30). */
  topRight?: preact.ComponentChildren;
  /** In a fix: Accept all N remaining, right from the card (Kristi, Oct 30; words Nov 1). */
  onAcceptRemaining?: () => void;
  remaining?: number;
  onSkip: () => void;
  onSaved: (suggestionIds: string[]) => void;
  onUndone: () => void;
}) {
  const notice = useNotice();
  const { path } = useRoute();
  const lists = useLists();
  const [book, setBook] = useState<BookData | null>(null);
  const [before, setBefore] = useState<Draft | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const [leaveOut, setLeaveOut] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [passing, setPassing] = useState(false);

  const load = () =>
    loadBook(head.label).then((b) => {
      setBook(b);
      const d = draftOf(b, b.status);
      setBefore({ ...d, audience: b.work.audience, where: 'keep' });
      setDraft((old) => old ?? d);
    });
  useEffect(() => {
    void load().catch((e: unknown) => notice({ message: `Couldn’t open ${head.title}: ${e instanceof Error ? e.message : String(e)}`, done: false }));
  }, [head.label]);

  if (!book || !draft || !before) return <p class="shelves-quiet">Loading {head.title}…</p>;

  // Genres and subgenres as their full names (a book may hold a short name), so "unchanged" is exact.
  const subAll = (lists?.genres ?? []).flatMap((g) => g.subgenres);
  const norm = (d: Draft): Draft => (lists ? { ...d, genres: d.genres.map((g) => nameOf(g, lists.genres)), subgenres: d.subgenres.map((x) => nameOf(x, subAll)) } : d);
  const was = norm(before);
  const now = norm(draft);

  const suggested = [...new Set(suggestions.map((s) => s.field))];
  const sugOf = (f: string) => suggestions.find((s) => s.field === f);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft({ ...draft, [k]: v });
  const genreNames = now.genres;
  const genreOptions = lists ? genreChoices(lists.genres, draft.literary as Literary | null, genreNames) : genreNames;
  const subOptions = (lists?.genres ?? []).filter((g) => genreNames.includes(g.name)).flatMap((g) => g.subgenres.map((s) => s.name));
  const others = suggested.filter((f) => !CARD_FIELD[f]);
  // A suggestion still waiting (Kristi, Oct 30): not accepted, not changed, not left suggested.
  // Its pill is pale and dashed until accepted; Save and next accepts whatever is still waiting.
  const changedField = (f: string) => {
    const k = CARD_FIELD[f];
    return k ? JSON.stringify(now[k]) !== JSON.stringify(was[k]) : false;
  };
  const pending = (f: string) => Boolean(sugOf(f)) && !accepted.has(f) && !changedField(f) && !leaveOut.has(f);
  const accept = (f: string) => setAccepted(new Set([...accepted, f]));
  const waiting = suggested.filter(pending).length;

  // A suggested row: the dashed "suggested" tag, ✓ Accept, and what it was before (Look through them).
  const tag = (f: string) => {
    const s = sugOf(f);
    if (!s) return null;
    if (changedField(f)) return <span class="rw-tag mine">yours</span>;
    const on = accepted.has(f);
    return (
      <span class="rw-sug">
        <span class={`rw-tag${on ? ' ok' : ''}`}>{on ? '✓ accepted' : 'suggested'}</span>
        {showBefore && s.before !== undefined ? <span class="rw-quiet">was {valueText(f, s.before)}</span> : null}
        {!on ? (
          <button type="button" class="btn-link" onClick={() => accept(f)}>
            ✓ Accept
          </button>
        ) : null}
      </span>
    );
  };

  const save = async () => {
    setBusy(true);
    const plan = reviewPlan(was, now, suggested, [...leaveOut]);
    const actions: string[] = [];
    const call = async (fn: string, args: Record<string, unknown>) => {
      const a = await callAction(fn, { book: book.id, ...args });
      if (a) actions.push(a);
    };
    try {
      for (const [k, v] of plan.changes) {
        if (k === 'literary') await call('book_edit', { field: 'literary_genre', value: v });
        else if (k === 'audience') await call('work_audience_edit', { value: v });
        else if (k === 'genres') await call('book_edit', { field: 'genres', value: [...(v as string[]), ...(book.hiddenGenres ?? [])] });
        else if (k === 'subgenres') await call('book_edit', { field: 'subgenres', value: v });
        else if (k === 'readType') await call('book_set', { field: 'read_type', value: v });
        else if (k === 'tags') await call('book_edit', { field: 'tags', value: v });
        else if (k === 'interest') await call('book_set', { field: 'interest', value: v === null ? null : String(v) });
        else if (k === 'where') await call('book_move', { to_shelf: v, details: {} });
      }
      const res = await saveReview(book.id, actions, plan.accept, plan.replaced);
      const ids = suggestions.filter((s) => plan.accept.includes(s.field) || plan.replaced.includes(s.field)).map((s) => s.id);
      onSaved(ids);
      notice({
        message: `Reviewed ${book.work.title}`,
        undo: () =>
          void undoReview(res.group, res.marks)
            .then(() => {
              onUndone();
              notice({ message: `Undone: ${book.work.title} is as it was` });
            })
            .catch((e: unknown) => notice({ message: `Couldn’t undo: ${e instanceof Error ? e.message : String(e)}`, done: false })),
      });
    } catch (e) {
      setBusy(false);
      notice({ message: `Not saved: ${e instanceof Error ? e.message : String(e)}${actions.length ? '. What saved before it is in the book’s Recent changes.' : ''}`, done: false });
    }
  };

  const author = book.authors.join(', ');
  const added = book.addedAt ? addedText(book.addedAt, todayIn()) : null;
  const cover = head.cover;
  const choices = whereChoices(book.status);

  return (
    <div class="rw-box rw-card" data-book={book.id}>
      <div class="rw-card-top">
        <span class="rw-quiet">{whereLine}</span>
        {topRight}
      </div>
      <div class="rw-book">
        <button type="button" class="rw-cover" aria-label={`Open ${book.work.title}`} onClick={() => navigate(`/book/${bookLabel(book.label)}`, { state: { from: path } })}>
          {cover ? <img src={cover} alt="" /> : <span aria-hidden="true" />}
        </button>
        <div class="rw-book-text">
          <div class="rw-book-title">{book.work.title}</div>
          <div class="rw-quiet">{[author, added ? `added ${added}` : null, statusName(book.status)].filter(Boolean).join(' · ')}</div>
          {book.work.summary ? <p class="rw-summary">{book.work.summary}</p> : null}
        </div>
      </div>
      {suggested.length ? (
        <div class="rw-accept-all">
          <button type="button" class="btn-secondary" onClick={() => setAccepted(new Set(suggested))}>
            ✓ Accept all suggestions
          </button>
        </div>
      ) : null}
      <dl class="rw-rows">
        <Row label="Literary genre" extra={tag('fiction')}>
          <Pills options={LITERARY_CHOICES.map((c) => [c.key, c.name])} value={draft.literary} onPick={(v) => set('literary', v)} label="Literary genre" pending={pending('fiction')} onAccept={() => accept('fiction')} />
        </Row>
        <Row label="Audience">
          <Pills options={AUDIENCE_CHOICES.map((c) => [c.key, c.name])} value={draft.audience} onPick={(v) => set('audience', v)} label="Audience" />
        </Row>
        <Row label="Genres" extra={tag('genres')}>
          {lists ? <ChipsEditor label="Genres" options={genreOptions} value={genreNames} onChange={(v) => set('genres', v)} pending={pending('genres')} /> : <span class="rw-quiet">Loading your genres…</span>}
        </Row>
        <Row label="Subgenres" extra={tag('subgenres')}>
          {subOptions.length || draft.subgenres.length ? <ChipsEditor label="Subgenres" options={subOptions} value={now.subgenres} onChange={(v) => set('subgenres', v)} pending={pending('subgenres')} /> : <span class="rw-quiet">Pick a genre first</span>}
        </Row>
        <Row label="Read type" extra={tag('read_type')}>
          <Pills options={readTypes.map((n) => [n, n])} value={draft.readType} onPick={(v) => set('readType', v)} label="Read type" pending={pending('read_type')} onAccept={() => accept('read_type')} />
        </Row>
        <Row label="Tags" extra={tag('tags')}>
          <ChipsEditor label="Tags" options={lists?.tags ?? []} value={draft.tags} onChange={(v) => set('tags', v)} add pending={pending('tags')} />
        </Row>
        {others.map((f) => {
          const s = sugOf(f)!;
          const out = leaveOut.has(f);
          return (
            <Row key={f} label={FIELD_NAMES[f] ?? f}>
              <span class="rw-sug">
                <span class={`rw-tag${accepted.has(f) ? ' ok' : ''}`}>{out ? 'left as suggested' : accepted.has(f) ? '✓ accepted' : 'suggested'}</span>
                <span>{valueText(f, s.value)}</span>
                {!out && !accepted.has(f) ? (
                  <button type="button" class="btn-link" onClick={() => accept(f)}>
                    ✓ Accept
                  </button>
                ) : null}
                <button type="button" class="btn-link" onClick={() => setLeaveOut(new Set(out ? [...leaveOut].filter((x) => x !== f) : [...leaveOut, f]))}>
                  {out ? 'Accept it' : 'Not sure: leave it suggested'}
                </button>
              </span>
            </Row>
          );
        })}
        <Row label="Interest">
          <span class="rw-interest">
            <InterestDots value={draft.interest} hints={INTEREST_HINTS} onChange={(v) => set('interest', v)} />
            <span class="rw-quiet">{draft.interest === null ? 'Interest not set' : interestHint(draft.interest)}</span>
          </span>
        </Row>
        <Row label="Where it goes">
          <ChoiceChips class="rw-where" label="Where it goes" choices={choices.map((c) => [c.key as Where, c.name] as const)} value={draft.where} onPick={(k) => k && set('where', k)}>
            {book.status !== 'not_for_me' && book.status !== 'currently_reading' && book.status !== 'finished' ? <Chip plain label="Pass on it…" on={false} onToggle={() => setPassing(true)} /> : null}
          </ChoiceChips>
        </Row>
      </dl>
      <div class="rw-buttons">
        {onAcceptRemaining && remaining ? (
          <button type="button" class="btn-secondary rw-accept-rest" disabled={busy} onClick={onAcceptRemaining}>
            Accept all {num(remaining)} remaining
          </button>
        ) : null}
        <button type="button" class="btn-link" disabled={busy} onClick={onSkip}>
          Skip for now
        </button>
        <button type="button" class="btn-main" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : waiting ? `Accept ${waiting} ${waiting === 1 ? 'suggestion' : 'suggestions'} and save` : 'Save and next'}
        </button>
      </div>
      <p class="rw-quiet rw-note">Save marks it reviewed today (shown in My relation to the book). One Undo takes the whole review back.</p>
      {passing ? (
        <BookSheet
          label={book.label}
          title={book.work.title}
          what={{ kind: 'not_for_me' }}
          onClose={() => {
            setPassing(false);
            // It's on Not for me now (or still where it was, if cancelled): stay on the card to Save.
            void loadBook(head.label).then((b) => {
              setBook(b);
              setDraft((d) => (d ? { ...d, where: 'keep' } : d));
            });
          }}
        />
      ) : null}
    </div>
  );
}

function Row({ label, extra, children }: { label: string; extra?: preact.ComponentChildren; children: preact.ComponentChildren }) {
  return (
    <div class="rw-row">
      <dt>{label}</dt>
      <dd>
        {children}
        {extra ? <div class="rw-extra">{extra}</div> : null}
      </dd>
    </div>
  );
}

/** One choice of several; tap the chosen one again to clear it. */
function Pills({
  options,
  value,
  onPick,
  label,
  pending,
  onAccept,
}: {
  options: [string, string][];
  value: string | null;
  onPick: (v: string | null) => void;
  label: string;
  /** The chosen value is a suggestion still waiting: pale and dashed; tapping it accepts it. */
  pending?: boolean;
  onAccept?: () => void;
}) {
  return <ChoiceChips class="rw-pills" label={label} small clearable choices={options} value={value} onPick={onPick} pending={pending} onAccept={onAccept} />;
}

type RowDone = { kind: 'accepted' | 'changed' | 'reviewed'; marks: string[]; action?: string | null; value?: string };
type Snap = Record<string, { field: string; before: unknown; value: unknown }>;
type Looking = { name: string; ids: string[]; snap?: Snap };

/** A row's status when this visit has no record of it (after Back from a book page): from the book itself. */
export function rowStatus(b: Pick<ShelfBook, 'readTypeName' | 'literary' | 'lastReviewedAt'>, field: string, suggested: unknown): { kind: 'accepted' | 'changed' | 'reviewed'; value: string } {
  const now = field === 'read_type' ? b.readTypeName ?? null : field === 'fiction' ? b.literary ?? null : null;
  const value = now === null ? valueText(field, suggested) : valueText(field, now);
  if (b.lastReviewedAt) return { kind: 'reviewed', value };
  if (now === null || now === suggested) return { kind: 'accepted', value };
  return { kind: 'changed', value };
}

const RETURN_KEY = 'queue:review:return';


/**
 * Look through them (Kristi, Oct 30, option A): a fix's books 20 at a time, each with what it was
 * and what's suggested; ✓ Accept (or tap the dashed value) and Change save at once, with Undo on the
 * row and in the notice; See details shows the one-book card; the title opens the book page. A
 * handled row keeps its values and says what happened. Accept N shown and Accept all N remaining
 * stay pinned at the top. Accepting here doesn't mark a book reviewed.
 */
function FixList({
  name,
  ids,
  byId,
  suggestions,
  snap,
  handled,
  setHandled,
  readTypes,
  onOpen,
  onAcceptRemaining,
  setSuggestions,
  reload,
}: {
  name: string;
  ids: string[];
  byId: Map<string, ShelfBook>;
  suggestions: Suggestion[];
  snap: Snap;
  handled: Record<string, RowDone>;
  setHandled: (f: (h: Record<string, RowDone>) => Record<string, RowDone>) => void;
  readTypes: string[];
  onOpen: (id: string) => void;
  onAcceptRemaining?: () => void;
  setSuggestions: (f: (s: Suggestion[] | null) => Suggestion[] | null) => void;
  reload: () => Promise<void>;
}) {
  const notice = useNotice();
  const wide = useWide();
  const topRef = useTopLine();
  const { path } = useRoute();
  const [shownKept, setShownKept] = useRemembered<{ name: string; n: number }>('queue:review:shown', { name, n: 20 });
  const shown = shownKept.name === name ? shownKept.n : 20;
  const setShown = (n: number) => setShownKept({ name, n });
  const [details, setDetails] = useState<Map<string, Details>>(new Map());
  const [busy, setBusy] = useState<string | null>(null);
  const waiting = (id: string) => suggestions.filter((x) => x.bookId === id);
  // Sorted by its headers (R6, Kristi Nov 2), opening in the fix's own order; a third tap goes back to it.
  const [sort, setSort] = useState<{ key: FixSortKey; reversed: boolean } | null>(null);
  const kindText = (b: ShelfBook) => [b.literary ? valueText('fiction', b.literary) : null, details.get(b.id)?.genre ?? b.genre].filter(Boolean).join(' · ');
  const sugText = (id: string) => {
    const x = waiting(id)[0] ?? snap[id];
    return x ? valueText(x.field, x.value) : '';
  };
  const sorted = useMemo(
    () =>
      sort
        ? sortList(ids, (id) => {
            const b = byId.get(id)!;
            if (sort.key === 'title') return titleKey(b.title);
            if (sort.key === 'genre') return kindText(b).toLocaleLowerCase('en-US') || null;
            return sugText(id).toLocaleLowerCase('en-US') || null;
          }, sort.reversed)
        : ids,
    [ids.join(','), sort, suggestions],
  );
  const visible = sorted.slice(0, shown);
  const remaining = ids.filter((id) => waiting(id).length).length;
  const shownWaiting = visible.filter((id) => waiting(id).length);
  const mark = (id: string, r: RowDone | null) =>
    setHandled((old) => {
      const m = { ...old };
      if (r) m[id] = r;
      else delete m[id];
      return m;
    });

  useEffect(() => {
    const missing = visible.filter((id) => !details.has(id)).map((id) => byId.get(id)!).filter(Boolean);
    if (!missing.length) return;
    let live = true;
    loadDetails(missing, new Set())
      .then((m) => live && setDetails((old) => new Map([...old, ...m])))
      .catch(() => null);
    return () => {
      live = false;
    };
  }, [visible.join(',')]);

  // Back from a book page (phone): the same fix, enough rows shown, and that book's row in view.
  useEffect(() => {
    const back = recall<string | null>(RETURN_KEY, null);
    if (!back) return;
    keep(RETURN_KEY, null);
    const at = ids.indexOf(back);
    if (at >= shown) setShown(Math.ceil((at + 1) / 20) * 20);
    requestAnimationFrame(() => document.querySelector(`.rw-fix-row[data-row="${back}"]`)?.scrollIntoView({ block: 'center' }));
  }, []);

  const openPage = (id: string) => {
    const b = byId.get(id)!;
    if (!wide) keep(RETURN_KEY, id);
    navigate(`/book/${bookLabel(b.label)}`, { state: { from: path }, keepScroll: wide });
  };

  // Accept N shown (Kristi, Oct 30): the rows on screen still waiting, one Undo. Hidden once all are shown.
  const acceptShown = async () => {
    const rows = shownWaiting.map((id) => [id, waiting(id).map((x) => x.id)] as [string, string[]]);
    const sent = rows.flatMap(([, m]) => m);
    try {
      const marks = await markChanges(sent, 'accepted');
      setSuggestions((s) => (s ?? []).filter((x) => !sent.includes(x.id)));
      setHandled((old) => ({ ...old, ...Object.fromEntries(rows.map(([id, m]) => [id, { kind: 'accepted', marks: m }])) }));
      notice({
        message: `Accepted ${num(rows.length)} shown`,
        undo: () =>
          void unaccept(marks)
            .then(() => {
              setHandled((old) => {
                const m = { ...old };
                for (const [id] of rows) delete m[id];
                return m;
              });
              return reload();
            })
            .then(() => notice({ message: 'Undone: suggested again' })),
      });
    } catch (e) {
      notice({ message: `Not saved: ${e instanceof Error ? e.message : String(e)}`, done: false });
    }
  };

  const accept = async (id: string) => {
    const b = byId.get(id)!;
    const sent = waiting(id).map((x) => x.id);
    setBusy(id);
    try {
      const marks = await markChanges(sent, 'accepted');
      setSuggestions((s) => (s ?? []).filter((x) => !sent.includes(x.id)));
      const r: RowDone = { kind: 'accepted', marks };
      mark(id, r);
      notice({ message: `Accepted for ${b.title}`, undo: () => void undo(id, r) });
    } catch (e) {
      notice({ message: `Not saved: ${e instanceof Error ? e.message : String(e)}`, done: false });
    }
    setBusy(null);
  };

  const change = async (id: string, field: string, value: string) => {
    const b = byId.get(id)!;
    const sugs = waiting(id).filter((x) => x.field === field);
    setBusy(id);
    try {
      // The mark first, then the value: if the value can't be saved, the mark goes back, so
      // nothing is left half done.
      const sent = sugs.map((x) => x.id);
      const marks = await markChanges(sent, 'replaced');
      let action: string | null;
      try {
        action =
          field === 'fiction'
            ? await callAction('book_edit', { book: id, field: 'literary_genre', value })
            : await callAction('book_set', { book: id, field: 'read_type', value });
      } catch (e) {
        await unaccept(marks).catch(() => null);
        throw e;
      }
      setSuggestions((s) => (s ?? []).filter((x) => !sent.includes(x.id)));
      const shownValue = field === 'fiction' ? valueText('fiction', value) : value;
      const r: RowDone = { kind: 'changed', marks, action, value: shownValue };
      mark(id, r);
      notice({ message: `${b.title}: ${shownValue}`, undo: () => void undo(id, r) });
    } catch (e) {
      notice({ message: `Not saved: ${e instanceof Error ? e.message : String(e)}`, done: false });
    }
    setBusy(null);
  };

  const undo = async (id: string, r: RowDone) => {
    try {
      if (r.action) await undoAction(r.action);
      await unaccept(r.marks);
      mark(id, null);
      await reload();
      notice({ message: `Undone: ${byId.get(id)?.title ?? 'the book'} is suggested again` });
    } catch (e) {
      notice({ message: `Couldn’t undo: ${e instanceof Error ? e.message : String(e)}`, done: false });
    }
  };

  const first = suggestions[0] ?? Object.values(snap)[0] ?? null;
  const STATUS = { accepted: '✓ Accepted', changed: '✓ Changed', reviewed: '✓ Reviewed' } as const;
  // The accept buttons: in the list's pinned top on a laptop and iPad; on a phone a bar at the bottom
  // of the screen, like Select several's, so only one layer pins at the top (check-up, Kristi Nov 2).
  const acceptButtons =
    (shownWaiting.length && shown < ids.length) || (remaining && onAcceptRemaining) ? (
      <>
        {shownWaiting.length && shown < ids.length ? (
          <button type="button" class="btn-secondary" onClick={() => void acceptShown()}>
            Accept {num(shownWaiting.length)} shown
          </button>
        ) : null}
        {remaining && onAcceptRemaining ? (
          <button type="button" class="btn-main" onClick={onAcceptRemaining}>
            Accept all {num(remaining)} remaining
          </button>
        ) : null}
      </>
    ) : null;
  return (
    <div class="rw-list rw-fixlist">
      <div class="rw-fix-top" ref={topRef}>
        <span class="rw-box-name">{name}</span>
        <span class="rw-grow" />
        {/* A phone has no header row: its sort is the Sort pill (R6 T8), in step with the headers. */}
        {!wide ? (
          <SettingMenu<FixSortKey | 'list'>
            name="Sort"
            choices={[['list', 'The fix’s sequence'], ['title', 'Title'], ['genre', 'Genre'], ['suggestion', 'Suggestion']]}
            value={sort?.key ?? 'list'}
            onChange={(k) => setSort(k === 'list' ? null : { key: k, reversed: false })}
          />
        ) : null}
        {wide ? acceptButtons : null}
      </div>
      <p class="rw-quiet rw-fix-line">
        {num(ids.length)} {ids.length === 1 ? 'book' : 'books'} · {remaining ? `${num(remaining)} still waiting (dashed)` : 'none waiting'}
        {first?.field ? ` · ${FIELD_NAMES[first.field] ?? first.field}` : ''}
      </p>
      <p class="rw-quiet rw-fix-explain">These books already have the value your rule set. Accept confirms it; Change picks another. Accept all is one Undo.</p>
      <ListTable<FixSortKey>
        label={name}
        cols={FIX_COLS}
        sort={sort ? { key: sort.key, desc: sort.reversed } : null}
        onSort={(k) => setSort(cycleSort(sort, k))}
        stickyTop="calc(var(--top-bar-h, 0px) + var(--queue-top-h, 0px) + var(--rw-top-h, 0px))"
        empty="Nothing in this fix."
        foot={<ListFoot shown={Math.min(shown, ids.length)} total={ids.length} page={20} onMore={() => setShown(shown + 20)} onAll={() => setShown(ids.length)} onFewer={() => setShown(20)} />}
      >
        {visible.map((id) => {
          const b = byId.get(id)!;
          const d = details.get(id);
          const sugs = waiting(id);
          const was = snap[id] ?? (sugs[0] ? { field: sugs[0].field, before: sugs[0].before, value: sugs[0].value } : { field: '', before: null, value: null });
          const done = sugs.length ? null : handled[id] ?? null;
          const status = sugs.length ? null : done ? { kind: done.kind, value: done.value ?? rowStatus(b, was.field, was.value).value } : rowStatus(b, was.field, was.value);
          const canChange = sugs.length === 1 && (sugs[0]!.field === 'read_type' || sugs[0]!.field === 'fiction');
          const before = was.before !== undefined && was.before !== null ? <span class="rw-quiet">{valueText(was.field, was.before)} → </span> : null;
          const book = <BookCell title={b.title} cover={d?.cover ?? null} author={d?.author ?? b.author} onOpen={() => openPage(id)} />;
          const kind = <span class="rw-quiet rw-fix-kind">{[b.literary ? valueText('fiction', b.literary) : null, d?.genre ?? b.genre].filter(Boolean).join(' · ')}</span>;
          const value = (
            <span class="rw-fix-value">
              {sugs.length ? (
                sugs.map((x) => (
                  <span key={x.id} class="rw-fix-change">
                    {x.before !== undefined && x.before !== null ? <span class="rw-quiet">{valueText(x.field, x.before)} → </span> : null}
                    <Chip small sug plain on title="Suggested: tap to accept" disabled={busy === id} label={valueText(x.field, x.value)} onToggle={() => void accept(id)} />
                  </span>
                ))
              ) : (
                <span class="rw-fix-change">
                  {before}
                  {/* A settled value is a pill, never a filled chip (R6 T7). */}
                  <span class="pill">{status!.value}</span>
                </span>
              )}
            </span>
          );
          const actions = (
            <span class="rw-fix-actions">
              {sugs.length ? (
                <>
                  <button type="button" class="btn-secondary" disabled={busy === id} onClick={() => void accept(id)}>
                    ✓ Accept
                  </button>
                  {canChange ? (
                    <PillMenu<string>
                      label="Change"
                      ariaLabel={`Change ${b.title}`}
                      choices={(sugs[0]!.field === 'fiction' ? LITERARY_CHOICES.map((c) => [c.key, c.name] as [string, string]) : readTypes.map((n) => [n, n] as [string, string]))
                        .filter(([k]) => k !== sugs[0]!.value)
                        .map(([k, n]) => ({ value: k, label: n }))}
                      onPick={(v) => void change(id, sugs[0]!.field, v)}
                    />
                  ) : null}
                </>
              ) : (
                <span class="rw-fix-status">
                  {STATUS[status!.kind]}
                  {done && done.kind !== 'reviewed' ? (
                    <>
                      {' · '}
                      <button type="button" class="btn-link" onClick={() => void undo(id, done)}>
                        Undo
                      </button>
                    </>
                  ) : null}
                </span>
              )}
              <button type="button" class="btn-link rw-fix-details" onClick={() => onOpen(id)}>
                See details
              </button>
            </span>
          );
          return (
            <ListRow
              key={id}
              class="rw-fix-row"
              data-row={id}
              quiet={!sugs.length}
              cells={[book, kind, value, actions]}
              phone={{
                book,
                line: (
                  <span class="rw-phone-line">
                    {kind}
                    {value}
                    {actions}
                  </span>
                ),
              }}
            />
          );
        })}
      </ListTable>
      {!wide && acceptButtons ? (
        <div class="queue-bulk rw-accept-bar" role="region" aria-label="Accept suggestions">
          {acceptButtons}
        </div>
      ) : null}
    </div>
  );
}

type FixSortKey = 'title' | 'genre' | 'suggestion';
// Book · Genre · Suggestion, and the row's buttons with no header (R6 T3).
const FIX_COLS: Col<FixSortKey>[] = [
  { label: 'Book', width: 'minmax(0, 1.6fr)', sort: 'title', book: true },
  { label: 'Genre', width: 'minmax(0, 1fr)', sort: 'genre' },
  { label: 'Suggestion', width: 'minmax(0, 1.2fr)', sort: 'suggestion' },
  { width: '300px' },
];

type NewSortKey = 'added' | 'title' | 'kind' | 'readtype' | 'interest' | 'shelf';
// Added · Book · Genre · Read type · Interest · Shelf, and the row's buttons with no header (R6 T3).
const NEW_COLS: Col<NewSortKey>[] = [
  { label: 'Added', width: '70px', sort: 'added' },
  { label: 'Book', width: 'minmax(0, 1.5fr)', sort: 'title', book: true },
  { label: 'Genre', width: 'minmax(0, 1.2fr)', sort: 'kind' },
  { label: 'Read type', width: '140px', sort: 'readtype' },
  { label: 'Interest', width: 'var(--q-interest-w)', sort: 'interest' },
  { label: 'Shelf', width: '200px', sort: 'shelf' },
  { width: '200px' },
];

/** List · One at a time, for New books (Kristi, Oct 30). */
function ViewSwitch({ value, onPick }: { value: 'list' | 'one'; onPick: (v: 'list' | 'one') => void }) {
  return <Segmented label="Show new books" options={[['list', 'List'], ['one', 'One at a time']]} value={value} onChange={onPick} />;
}

/**
 * New books as a quick list (Kristi, Oct 30, from the chat mockup): literary genre · genre (changed
 * in See details), read type, Interest and Move in the row, each saving at once with Undo; a dashed
 * suggestion accepts with a tap; ✓ Reviewed accepts anything still dashed and marks the book
 * reviewed. A reviewed row stays until you leave. No "review them all": each book is looked at.
 */
function NewList({
  ids,
  byId,
  suggestions,
  handled,
  setHandled,
  readTypes,
  onOpen,
  onOneAtATime,
  setSuggestions,
  reload,
}: {
  ids: string[];
  byId: Map<string, ShelfBook>;
  suggestions: Suggestion[];
  handled: Record<string, RowDone>;
  setHandled: (f: (h: Record<string, RowDone>) => Record<string, RowDone>) => void;
  readTypes: string[];
  onOpen: (id: string) => void;
  onOneAtATime: () => void;
  setSuggestions: (f: (s: Suggestion[] | null) => Suggestion[] | null) => void;
  reload: () => Promise<void>;
}) {
  const notice = useNotice();
  const wide = useWide();
  const narrowInterest = useMatches(NARROW_INTEREST);
  const editOn = useFlag('book_page.edit');
  const { path } = useRoute();
  const [shown, setShown] = useState(20);
  const [details, setDetails] = useState<Map<string, Details>>(new Map());
  const [sheet, setSheet] = useState<{ id: string; what: BookSheetKind } | null>(null);
  const topRef = useTopLine();
  const waiting = (id: string) => suggestions.filter((x) => x.bookId === id);
  // Sorted by its headers (R6, Kristi Nov 2), opening newest added first as today.
  const [sort, setSort] = useState<{ key: NewSortKey; reversed: boolean }>({ key: 'added', reversed: false });
  const sorted = useMemo(
    () =>
      sortList(
        ids,
        (id) => {
          const b = byId.get(id)!;
          switch (sort.key) {
            case 'added':
              return b.addedAt ? -Date.parse(b.addedAt) : null; // newest first
            case 'title':
              return titleKey(b.title);
            case 'kind':
              return [b.literary ? valueText('fiction', b.literary) : null, b.genre].filter(Boolean).join(' · ').toLocaleLowerCase('en-US') || null;
            case 'readtype':
              return b.readTypeName?.toLocaleLowerCase('en-US') ?? null;
            case 'interest':
              return b.interest === null ? null : -b.interest; // highest first
            case 'shelf':
              return shelfRank(b);
          }
        },
        sort.reversed,
      ),
    [ids.join(','), sort, byId],
  );
  const visible = sorted.slice(0, shown);

  useEffect(() => {
    const missing = visible.filter((id) => !details.has(id)).map((id) => byId.get(id)!).filter(Boolean);
    if (!missing.length) return;
    let live = true;
    loadDetails(missing, new Set())
      .then((m) => live && setDetails((old) => new Map([...old, ...m])))
      .catch(() => null);
    return () => {
      live = false;
    };
  }, [visible.join(',')]);

  useEffect(() => {
    const back = recall<string | null>(RETURN_KEY, null);
    if (!back) return;
    keep(RETURN_KEY, null);
    const at = ids.indexOf(back);
    if (at >= shown) setShown(Math.ceil((at + 1) / 20) * 20);
    requestAnimationFrame(() => document.querySelector(`.rw-new-row[data-row="${back}"]`)?.scrollIntoView({ block: 'center' }));
  }, []);

  const openPage = (id: string) => {
    const b = byId.get(id)!;
    if (!wide) keep(RETURN_KEY, id);
    navigate(`/book/${bookLabel(b.label)}`, { state: { from: path }, keepScroll: wide });
  };
  const fail = (e: unknown) => notice({ message: `Not saved: ${e instanceof Error ? e.message : String(e)}`, done: false });

  // One change in the row: the book's usual function, then (if a suggestion was waiting on that
  // field) the mark taken off as replaced. Undo puts both back.
  const set = async (id: string, field: 'read_type' | 'interest', value: string | null, message: string) => {
    const sugs = waiting(id).filter((x) => x.field === field);
    try {
      const marks = sugs.length ? await markChanges(sugs.map((x) => x.id), 'replaced') : [];
      let action: string | null;
      try {
        action = await callAction('book_set', { book: id, field, value });
      } catch (e) {
        if (marks.length) await unaccept(marks).catch(() => null);
        throw e;
      }
      if (marks.length) setSuggestions((s) => (s ?? []).filter((x) => !marks.includes(x.id)));
      notice({
        message,
        undo: action
          ? () =>
              void undoAction(action!)
                .then(() => (marks.length ? unaccept(marks) : 0))
                .then(() => reload())
                .then(() => notice({ message: 'Undone' }))
          : undefined,
      });
    } catch (e) {
      fail(e);
    }
  };

  const acceptOne = async (s: Suggestion) => {
    try {
      const marks = await markChanges([s.id], 'accepted');
      setSuggestions((x) => (x ?? []).filter((y) => y.id !== s.id));
      notice({ message: `Accepted: ${valueText(s.field, s.value)}`, undo: () => void unaccept(marks).then(() => reload()) });
    } catch (e) {
      fail(e);
    }
  };

  const reviewed = async (id: string) => {
    const b = byId.get(id)!;
    try {
      const fields = [...new Set(waiting(id).map((x) => x.field))];
      const res = await saveReview(id, [], fields, []);
      const sent = waiting(id).map((x) => x.id);
      setSuggestions((s) => (s ?? []).filter((x) => !sent.includes(x.id)));
      const r: RowDone = { kind: 'reviewed', marks: res.marks, action: res.group };
      setHandled((old) => ({ ...old, [id]: r }));
      notice({ message: `Reviewed ${b.title}`, undo: () => void undoRow(id, r) });
    } catch (e) {
      fail(e);
    }
  };
  const undoRow = async (id: string, r: RowDone) => {
    try {
      await undoReview(r.action!, r.marks);
      setHandled((old) => {
        const m = { ...old };
        delete m[id];
        return m;
      });
      await reload();
      notice({ message: `Undone: ${byId.get(id)?.title ?? 'the book'} isn’t reviewed` });
    } catch (e) {
      notice({ message: `Couldn’t undo: ${e instanceof Error ? e.message : String(e)}`, done: false });
    }
  };

  const sugTag = (s: Suggestion) => (
    <Chip key={s.id} small sug plain on title="Suggested: tap to accept" label={valueText(s.field, s.value)} onToggle={() => void acceptOne(s)} />
  );

  return (
    <div class="rw-list rw-newlist">
      <div class="rw-fix-top" ref={topRef}>
        <span class="rw-box-name">New books</span>
        <span class="rw-quiet">Added by hand or by Claude, not reviewed yet</span>
        <span class="rw-grow" />
        {!wide ? (
          <SettingMenu<NewSortKey>
            name="Sort"
            choices={[['added', 'Newest added'], ['title', 'Title'], ['kind', 'Genre'], ['readtype', 'Read type'], ['interest', 'Interest'], ['shelf', 'Shelf']]}
            value={sort.key}
            onChange={(k) => setSort({ key: k, reversed: false })}
          />
        ) : null}
        <ViewSwitch value="list" onPick={(v) => v === 'one' && onOneAtATime()} />
      </div>
      <ListTable<NewSortKey>
        label="New books"
        cols={NEW_COLS}
        sort={{ key: sort.key, desc: sort.reversed }}
        onSort={(k) => setSort(flipSort(sort, k))}
        stickyTop="calc(var(--top-bar-h, 0px) + var(--queue-top-h, 0px) + var(--rw-top-h, 0px))"
        empty="No new books to review: everything you’ve added has been reviewed."
        foot={<ListFoot shown={Math.min(shown, ids.length)} total={ids.length} page={20} onMore={() => setShown(shown + 20)} onAll={() => setShown(ids.length)} onFewer={() => setShown(20)} />}
      >
        {visible.map((id) => {
          const b = byId.get(id)!;
          const d = details.get(id);
          const sugs = waiting(id);
          const sugOf = (f: string) => sugs.find((x) => x.field === f);
          const done = handled[id] ?? null;
          const row = { ...b, ...d } as ShelfBook;
          const added = b.addedAt ? addedText(b.addedAt, todayIn()) : '';
          const book = <BookCell title={b.title} cover={d?.cover ?? null} author={d?.author ?? b.author} onOpen={() => openPage(id)} />;
          const kind = (
            <span class="rw-new-kind">
              {/* One link to the details card, where these change (Kristi, Oct 30); suggested parts dashed. */}
              <button type="button" class="btn-link rw-kind-link" title="Change in See details" onClick={() => onOpen(id)}>
                <span class={sugOf('fiction') ? 'rw-sug-text' : ''}>{sugOf('fiction') ? valueText('fiction', sugOf('fiction')!.value) : b.literary ? valueText('fiction', b.literary) : 'No literary genre'}</span>
                {' · '}
                <span class={sugOf('genres') ? 'rw-sug-text' : ''}>{sugOf('genres') ? valueText('genres', sugOf('genres')!.value) : d?.genre ?? b.genre ?? 'no genre'}</span>
              </button>
            </span>
          );
          const type = (
            <span class="rw-new-type">
              {sugOf('read_type') ? (
                <>
                  {sugTag(sugOf('read_type')!)}
                  <TypeMenu row={{ ...row, readTypeName: null, readType: null, title: b.title }} names={readTypes} onPick={(n) => void set(id, 'read_type', n, n ? `${b.title}: ${n}` : `Read type cleared for ${b.title}`)} />
                </>
              ) : (
                <TypeMenu row={row} names={readTypes} onPick={(n) => void set(id, 'read_type', n, n ? `${b.title}: ${n}` : `Read type cleared for ${b.title}`)} />
              )}
            </span>
          );
          const interest = (
            <span class="rw-new-interest">
              {wide && !narrowInterest ? (
                <InterestDots value={b.interest} hints={INTEREST_HINTS} onChange={(v) => void set(id, 'interest', v === null ? null : String(v), v === null ? `Interest cleared for ${b.title}` : `Interest set to ${interestHint(v)}`)} />
              ) : (
                <InterestMenu row={b} onPick={(v) => void set(id, 'interest', v === null ? null : String(v), v === null ? `Interest cleared for ${b.title}` : `Interest set to ${interestHint(v)}`)} />
              )}
            </span>
          );
          const shelf = (
            <span class="rw-new-shelf">
              <span class={`pill${b.status === null ? ' quiet' : ''}`}>{statusName(b.status)}</span>
              <MoveMenu row={{ title: b.title, status: b.status }} pastRead={Boolean(editOn)} onSheet={(what) => setSheet({ id, what })} />
            </span>
          );
          const actions = (
            <span class="rw-fix-actions">
              {done ? (
                <span class="rw-fix-status">
                  ✓ Reviewed ·{' '}
                  <button type="button" class="btn-link" onClick={() => void undoRow(id, done)}>
                    Undo
                  </button>
                </span>
              ) : (
                <button type="button" class="btn-secondary" onClick={() => void reviewed(id)}>
                  ✓ Reviewed
                </button>
              )}
              <button type="button" class="btn-link rw-fix-details" onClick={() => onOpen(id)}>
                See details
              </button>
            </span>
          );
          return (
            <ListRow
              key={id}
              class={`rw-new-row${done ? ' done' : ''}`}
              data-row={id}
              quiet={Boolean(done)}
              cells={[<span class="rw-new-added">{added}</span>, book, kind, type, interest, shelf, actions]}
              phone={{
                book: <BookCell title={b.title} cover={d?.cover ?? null} author={[d?.author ?? b.author, added ? `added ${added}` : null].filter(Boolean).join(' · ')} onOpen={() => openPage(id)} />,
                line: (
                  <span class="rw-phone-line">
                    {kind}
                    <span class="q-controls">
                      {type}
                      {interest}
                      {shelf}
                    </span>
                    {actions}
                  </span>
                ),
              }}
            />
          );
        })}
      </ListTable>
      {sheet ? <BookSheet label={byId.get(sheet.id)!.label} title={byId.get(sheet.id)!.title} what={sheet.what} onClose={() => setSheet(null)} /> : null}
    </div>
  );
}
